import { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { otpEndpoint } from '../../services/api/endpoints/otp.endpoint';
import { logger } from '../../utils/logger';

interface OtpVerificationState {
  isSending: boolean;
  isVerifying: boolean;
  isVerified: boolean;
  error: string | null;
  cooldown: number;
}

export const useOtpVerification = () => {
  const clientId = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation?.id ?? null
  );

  const [state, setState] = useState<OtpVerificationState>({
    isSending: false,
    isVerifying: false,
    isVerified: false,
    error: null,
    cooldown: 0,
  });

  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current);
      }
    };
  }, []);

  const startCooldown = useCallback(() => {
    setState(prev => ({ ...prev, cooldown: 60 }));
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setState(prev => {
        if (prev.cooldown <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          cooldownRef.current = null;
          return { ...prev, cooldown: 0 };
        }
        return { ...prev, cooldown: prev.cooldown - 1 };
      });
    }, 1000);
  }, []);

  const sendCode = useCallback(
    async (phoneNumber: string) => {
      if (!clientId) {
        setState(prev => ({ ...prev, error: 'Organisation not found.' }));
        return;
      }
      setState(prev => ({ ...prev, isSending: true, error: null }));
      try {
        await otpEndpoint.sendOtp(clientId, phoneNumber);
        startCooldown();
        setState(prev => ({ ...prev, isSending: false }));
      } catch (err) {
        logger.error('useOtpVerification: sendCode failed', err);
        setState(prev => ({
          ...prev,
          isSending: false,
          error: err instanceof Error ? err.message : 'Failed to send verification code.',
        }));
      }
    },
    [clientId, startCooldown]
  );

  const verifyCode = useCallback(
    async (phoneNumber: string, otp: string) => {
      if (!clientId) {
        setState(prev => ({ ...prev, error: 'Organisation not found.' }));
        return false;
      }
      setState(prev => ({ ...prev, isVerifying: true, error: null }));
      try {
        await otpEndpoint.verifyOtp(clientId, phoneNumber, otp);
        setState(prev => ({ ...prev, isVerifying: false, isVerified: true }));
        if (cooldownRef.current) {
          clearInterval(cooldownRef.current);
          cooldownRef.current = null;
        }
        return true;
      } catch (err) {
        logger.error('useOtpVerification: verifyCode failed', err);
        setState(prev => ({
          ...prev,
          isVerifying: false,
          error: err instanceof Error ? err.message : 'Invalid verification code.',
        }));
        return false;
      }
    },
    [clientId]
  );

  const reset = useCallback(() => {
    if (cooldownRef.current) {
      clearInterval(cooldownRef.current);
      cooldownRef.current = null;
    }
    setState({
      isSending: false,
      isVerifying: false,
      isVerified: false,
      error: null,
      cooldown: 0,
    });
  }, []);

  return {
    ...state,
    sendCode,
    verifyCode,
    reset,
  };
};
